const BASE = '/spring/hospital/api';

export function redirectToLogin() {
  window.dispatchEvent(new CustomEvent('hospital-session-expired'));
}

export function goToLoginPage() {
  const returnTo = encodeURIComponent(window.location.href.split('?')[0]);
  window.location.href = '/spring/login?returnTo=' + returnTo;
}

export async function hospitalLogout() {
  try { await fetch('/spring/hospital/api/logout', { method: 'POST' }); } catch (_) {}
  goToLoginPage();
}

async function req(path, options = {}) {
  const res = await fetch(BASE + path, {
    headers: { 'Content-Type': 'application/json', ...options.headers },
    credentials: 'include',
    ...options,
  });
  if (res.redirected && res.url.includes('login')) { redirectToLogin(); return null; }
  if (res.status === 401 || res.status === 403) { redirectToLogin(); return null; }
  if (!res.ok) {
    const ct = res.headers.get('content-type') || '';
    if (ct.includes('text/html')) { redirectToLogin(); return null; }
    throw new Error(`${res.status} ${res.statusText}`);
  }
  if (res.status === 204) return null;
  const data = await res.json();
  if (data && data.error === 'Not authenticated') { redirectToLogin(); return null; }
  return data;
}

export const hospitalApi = {
  // Session
  getSession: () => req('/session'),

  // Hospital Info
  getInfo: () => req('/info'),
  upsertInfo: (d) => req('/info', { method: 'POST', body: JSON.stringify(d) }),

  // Dashboard
  getDashboard: () => req('/dashboard'),

  // Doctors
  getDoctors: () => req('/doctors'),
  createDoctor: (d) => req('/doctors', { method: 'POST', body: JSON.stringify(d) }),
  updateDoctor: (id, d) => req(`/doctors/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteDoctor: (id) => req(`/doctors/${id}`, { method: 'DELETE' }),

  // Patients
  getPatients: () => req('/patients'),
  getPatient: (id) => req(`/patients/${id}`),
  createPatient: (d) => req('/patients', { method: 'POST', body: JSON.stringify(d) }),
  updatePatient: (id, d) => req(`/patients/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deletePatient: (id) => req(`/patients/${id}`, { method: 'DELETE' }),

  // Appointments
  getAppointments: (date) => req('/appointments' + (date ? `?date=${date}` : '')),
  createAppointment: (d) => req('/appointments', { method: 'POST', body: JSON.stringify(d) }),
  updateAppointment: (id, d) => req(`/appointments/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  cancelAppointment: (id) => req(`/appointments/${id}`, { method: 'DELETE' }),

  // Treatments
  getTreatments: (patientId) => req('/treatments' + (patientId ? `?patientId=${patientId}` : '')),
  createTreatment: (d) => req('/treatments', { method: 'POST', body: JSON.stringify(d) }),
  updateTreatment: (id, d) => req(`/treatments/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  getPrescriptions: (treatmentId) => req(`/treatments/${treatmentId}/prescriptions`),
  addPrescription: (treatmentId, d) => req(`/treatments/${treatmentId}/prescriptions`, { method: 'POST', body: JSON.stringify(d) }),
  deletePrescription: (id) => req(`/prescriptions/${id}`, { method: 'DELETE' }),

  // Lab Reports
  getLabReports: (patientId) => req('/lab-reports' + (patientId ? `?patientId=${patientId}` : '')),
  createLabReport: (d) => req('/lab-reports', { method: 'POST', body: JSON.stringify(d) }),
  updateLabReport: (id, d) => req(`/lab-reports/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteLabReport: (id) => req(`/lab-reports/${id}`, { method: 'DELETE' }),

  // Invoices
  getInvoices: (status) => req('/invoices' + (status ? `?status=${status}` : '')),
  createInvoice: (d) => req('/invoices', { method: 'POST', body: JSON.stringify(d) }),
  payInvoice: (id, d) => req(`/invoices/${id}/pay`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteInvoice: (id) => req(`/invoices/${id}`, { method: 'DELETE' }),

  // Expenses
  getExpenses: (month) => req('/expenses' + (month ? `?month=${month}` : '')),
  getExpensesSummary: () => req('/expenses/summary'),
  createExpense: (d) => req('/expenses', { method: 'POST', body: JSON.stringify(d) }),
  updateExpense: (id, d) => req(`/expenses/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteExpense: (id) => req(`/expenses/${id}`, { method: 'DELETE' }),

  // Staff
  getStaff: () => req('/staff'),
  createStaff: (d) => req('/staff', { method: 'POST', body: JSON.stringify(d) }),
  updateStaff: (id, d) => req(`/staff/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteStaff: (id) => req(`/staff/${id}`, { method: 'DELETE' }),

  // AI Insights
  getCachedAiInsights: (patientId) => req(`/ai/patient/${patientId}`),
  getAiInsights: (patientId) => req(`/ai/patient/${patientId}`, { method: 'POST' }),

  // System Users (admin only)
  getSystemUsers: () => req('/users'),
  createSystemUser: (d) => req('/users', { method: 'POST', body: JSON.stringify(d) }),
  updateSystemUser: (id, d) => req(`/users/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteSystemUser: (id) => req(`/users/${id}`, { method: 'DELETE' }),
  resetSystemUserPassword: (id, d) => req(`/users/${id}/password`, { method: 'PUT', body: JSON.stringify(d) }),

  // IPD — Rooms
  getRooms: () => req('/ipd/rooms'),
  createRoom: (d) => req('/ipd/rooms', { method: 'POST', body: JSON.stringify(d) }),
  updateRoom: (id, d) => req(`/ipd/rooms/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteRoom: (id) => req(`/ipd/rooms/${id}`, { method: 'DELETE' }),

  // IPD — Admissions
  getAdmissions: (status) => req('/ipd/admissions' + (status ? `?status=${status}` : '')),
  createAdmission: (d) => req('/ipd/admissions', { method: 'POST', body: JSON.stringify(d) }),
  updateAdmission: (id, d) => req(`/ipd/admissions/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  dischargeAdmission: (id, d) => req(`/ipd/admissions/${id}/discharge`, { method: 'POST', body: JSON.stringify(d) }),

  // OPD — Visits
  getOpdVisits: (date) => req('/opd/visits' + (date ? `?date=${date}` : '')),
  createOpdVisit: (d) => req('/opd/visits', { method: 'POST', body: JSON.stringify(d) }),
  updateOpdVisit: (id, d) => req(`/opd/visits/${id}`, { method: 'PUT', body: JSON.stringify(d) }),

  // Purchases — Suppliers
  getSuppliers: () => req('/purchases/suppliers'),
  createSupplier: (d) => req('/purchases/suppliers', { method: 'POST', body: JSON.stringify(d) }),
  updateSupplier: (id, d) => req(`/purchases/suppliers/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteSupplier: (id) => req(`/purchases/suppliers/${id}`, { method: 'DELETE' }),

  // Purchases — Orders
  getPurchaseOrders: () => req('/purchases/orders'),
  getPurchaseOrder: (id) => req(`/purchases/orders/${id}`),
  createPurchaseOrder: (d) => req('/purchases/orders', { method: 'POST', body: JSON.stringify(d) }),
  updatePurchasePayment: (id, d) => req(`/purchases/orders/${id}/payment`, { method: 'PUT', body: JSON.stringify(d) }),
  deletePurchaseOrder: (id) => req(`/purchases/orders/${id}`, { method: 'DELETE' }),

  // Patient Account Management (admin only)
  getPatientAccounts: () => req('/patient-accounts'),
  setPatientAccount: (patientId, d) => req(`/patient-accounts/${patientId}`, { method: 'POST', body: JSON.stringify(d) }),
  resetPatientPassword: (patientId, d) => req(`/patient-accounts/${patientId}/password`, { method: 'PUT', body: JSON.stringify(d) }),
  togglePatientAccountStatus: (patientId, d) => req(`/patient-accounts/${patientId}/status`, { method: 'PUT', body: JSON.stringify(d) }),
  revokePatientAccount: (patientId) => req(`/patient-accounts/${patientId}`, { method: 'DELETE' }),

  // Patient Portal (patient session — called from HospitalPatientPortalApp)
  patientPortalMe: () => req('/patient-portal/me'),
  patientPortalRecords: () => req('/patient-portal/records'),
  patientPortalLogout: () => req('/patient-auth/logout', { method: 'POST' }),

  // Medicine Bills
  getMedicineBills: (status) => req('/medicine-bills' + (status ? `?status=${status}` : '')),
  getMedicineBill: (id) => req(`/medicine-bills/${id}`),
  createMedicineBill: (d) => req('/medicine-bills', { method: 'POST', body: JSON.stringify(d) }),
  payMedicineBill: (id, d) => req(`/medicine-bills/${id}/pay`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteMedicineBill: (id) => req(`/medicine-bills/${id}`, { method: 'DELETE' }),

  // Inventory — Items
  getInventoryItems: () => req('/inventory/items'),
  createInventoryItem: (d) => req('/inventory/items', { method: 'POST', body: JSON.stringify(d) }),
  updateInventoryItem: (id, d) => req(`/inventory/items/${id}`, { method: 'PUT', body: JSON.stringify(d) }),
  deleteInventoryItem: (id) => req(`/inventory/items/${id}`, { method: 'DELETE' }),
  adjustStock: (id, d) => req(`/inventory/items/${id}/adjust`, { method: 'POST', body: JSON.stringify(d) }),
  getStockMovements: (itemId) => req('/inventory/movements' + (itemId ? `?itemId=${itemId}` : '')),
};
